/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useBalanceForSymbol } from "../../hooks/useBalanceForSymbol";
import { useGetMaxAmountToWrap } from "../../hooks/wrap/useGetMaxAmountToWrap";
import "../../pages/Trade/index.css";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Token } from "../../store/global/TokenEnum";
import { Amount } from "../../types/amount";
import { getPriceForTokenByTokenList } from "../../utils/tokenListUtils";
import AmountInput from "./sections/AmountInput";
import { BalanceDetails } from "./sections/BalanceDetails";
import { BalancePercentageOptionsEnum, BalancePercentages } from "./sections/BalancePercentages";
import { TokenInputSection } from "./sections/TokenInputSection";
import { useDisplayOnClick } from "hooks/useDisplayOnClick";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { getAmount, handleBalanceOption } from "utils/tradeUtils";
import { OrderStatus } from "types/orderStatus";
import { InteractionStore } from "store/interactions/InteractionStore";
import { AmountLabel, getLabelType } from "./sections/AmountLabel";

export interface TradeLegTakerProps {
  symbol: Token;
  showPlusButton: boolean;
  isWaitingToAddToken: boolean;
  focusOnLoad: boolean;
  setIsWaitingToAddToken: React.Dispatch<React.SetStateAction<boolean>>;
  setIsChangingToken: React.Dispatch<React.SetStateAction<Token | undefined>>;
}

export const TradeLegTaker: React.FC<TradeLegTakerProps> = ({
  symbol,
  showPlusButton,
  isWaitingToAddToken,
  focusOnLoad,
  setIsWaitingToAddToken,
  setIsChangingToken,
}) => {
  const tokenList = useTokenListByChain();
  const [amountInputRef, setAmountInputRef] = useState<HTMLInputElement | null>(null);
  const [balancePercentageActive, setBalancePercentageActive] = useState<
    BalancePercentageOptionsEnum | undefined
  >();
  const { address, quote, network, isSending, takerTokens, orderStatus } = GlobalStore.useState(
    s => ({
      address: s.address,
      quote: s.quote,
      network: s.network,
      isSending: s.isSending,
      takerTokens: s.takerTokens,
      orderStatus: s.orderStatus,
    })
  );
  const tokenListPrice = getPriceForTokenByTokenList(symbol, tokenList);
  const quotePrice = quote?.takerTokens[symbol]?.priceUsd;
  const price = quotePrice ?? tokenListPrice;
  const balance = useBalanceForSymbol(symbol);
  const getMaxAmountToWrap = useGetMaxAmountToWrap();
  const tokenDecimals = tokenList?.[symbol]?.chainInfo.decimals ?? 18;
  const selectedToken = takerTokens.find(entry => entry.token === symbol);
  const tradeLegToOpenOnAmountBarClick = InteractionStore.useState(
    s => s.tradeLegToOpenOnAmountBarClick
  );
  const hasFocusedInput = useRef(false);

  const {
    isDisplayed: isInputFocused,
    toggleDisplay: onTradeLegClick,
    elementToClickRef: tradeLegRef,
    elementToDisplayRef: balancePercentagesRef,
    forceOpen: openBalancePercentages,
    forceClose: hideBalancePercentages,
  } = useDisplayOnClick();

  useEffect(() => {
    if (tradeLegToOpenOnAmountBarClick === symbol) {
      openBalancePercentages();
      InteractionStore.update(s => (s.tradeLegToOpenOnAmountBarClick = undefined));
    }
  }, [openBalancePercentages, symbol, tradeLegToOpenOnAmountBarClick]);
  const onFocus = () => {
    openBalancePercentages();
    if (amountInputRef !== null) {
      amountInputRef.focus();
    }
  };

  useEffect(() => {
    if (hasFocusedInput.current) return;
    openBalancePercentages();
    if (amountInputRef == null) return;
    amountInputRef?.focus();
    hasFocusedInput.current = true;
  }, [amountInputRef, openBalancePercentages]);

  const amount = useMemo(() => {
    if (symbol == null) return Amount.ZERO;
    return getAmount(symbol, quote, "taker", selectedToken);
  }, [quote, selectedToken, symbol]);

  if (tokenList == null) return null;
  const isTradeAmountLargerThanBalance = selectedToken?.amount?.value.gt(
    balance?.balance?.value ?? 0
  );
  return (
    <Container onFocus={onFocus}>
      <TradeRowWrapper
        ref={tradeLegRef as React.MutableRefObject<HTMLDivElement>}
        data-testid={"trade-leg"}
        isInputFocused={isInputFocused}
        onClick={() => {
          GlobalStore.update(s => {
            s.orderError = undefined;
          });
          if (isInputFocused) return;
          onTradeLegClick();
        }}
      >
        <TokenInputAndBalanceContainer>
          <TokenControlContainer>
            <TokenInputSection
              symbol={symbol}
              showPlusButton={showPlusButton}
              counterParty={"taker"}
              setIsWaitingToAddToken={setIsWaitingToAddToken}
              isWaitingToAddToken={isWaitingToAddToken}
              setIsChangingToken={setIsChangingToken}
              hideBalancePercentages={hideBalancePercentages}
            />
            {isInputFocused && (
              <AmountInput
                ref={ref => setAmountInputRef(ref)}
                setBalancePercentageActive={setBalancePercentageActive}
                symbol={symbol}
              />
            )}
            <AmountLabel
              amountToDisplay={
                amount.value.gt(0) ? amount.toDisplay(symbol, tokenList) : Amount.ZERO.toString()
              }
              labelType={getLabelType(amount)}
              isVisible={!isInputFocused}
            />
          </TokenControlContainer>
          {balance != null && (
            <BalanceDetails
              symbol={symbol}
              balance={balance}
              price={price ? price : Amount.ZERO}
              amount={selectedToken ? amount : Amount.ZERO}
              onBalanceClick={() => {
                hideBalancePercentages();
                setBalancePercentageActive(BalancePercentageOptionsEnum.Max);
                void handleBalanceOption(
                  symbol,
                  "taker",
                  BalancePercentageOptionsEnum.Max,
                  network,
                  balance,
                  getMaxAmountToWrap,
                  tokenDecimals
                );
              }}
              isBalanceShown={!isSending && address !== ""}
              isUsdValueShown={price?.value.gt(0) ?? false}
              isTradeAmountLargerThanBalance={isTradeAmountLargerThanBalance && address !== ""}
            />
          )}
        </TokenInputAndBalanceContainer>
        {address &&
          isInputFocused &&
          balance != null &&
          (orderStatus === OrderStatus.ENTRY || orderStatus === OrderStatus.QUOTING) && (
            <BalancePercentages
              ref={balancePercentagesRef as React.MutableRefObject<HTMLDivElement>}
              balancePercentageActive={balancePercentageActive}
              handlePercentageClick={balanceOption => {
                setTimeout(() => {
                  hideBalancePercentages();
                }, 400);
                setBalancePercentageActive(balanceOption);
                void handleBalanceOption(
                  symbol,
                  "taker",
                  balanceOption,
                  network,
                  balance,
                  getMaxAmountToWrap,
                  tokenDecimals
                );
              }}
            />
          )}
      </TradeRowWrapper>
    </Container>
  );
};

// Sub components

const Container = styled.div(() => {
  return { padding: "0px 10px", width: "100%" };
});

const TradeRowWrapper = styled(AnimateChangeInHeight)<{
  isInputFocused: boolean;
}>`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  border-radius: 24px;
  padding: 0px 14px;
  overflow: hidden;
  background: ${props =>
    props.isInputFocused ? props.theme.surface.main.tertiary.default : "none"};
`;

const TokenInputAndBalanceContainer = styled.div`
  display: flex;
  padding: 14px 2px 16px 0px;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
`;

const TokenControlContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`;
